import { esAsignacionFamiliarPlame } from './conceptosEstaticosPlanilla';

/**
 * Pago por jornal en regímenes no CONSU.
 * Plantilla ÷ días calendario del mes × días pagables (tareo).
 * Faltas/permisos/suspensión no pagan ni generan 0705.
 * La asignación familiar (0201) se trata como estático también en jornal.
 */

export const FORMA_PAGO_MENSUAL = 'mensual';
export const FORMA_PAGO_JORNAL = 'jornal';
export const FORMA_PAGO_MIXTO = 'mixto';

export const ORIGEN_DESCUENTO_DOMINICAL_JORNAL = 'descuento_dominical_jornal';
export const CODIGO_DESCUENTO_DOMINICAL_JORNAL = 'DOMJ';
export const ORIGEN_DESCUENTO_TARDANZA_JORNAL = 'descuento_tardanza_jornal';
export const CODIGO_DESCUENTO_TARDANZA_JORNAL = 'TARJ';

export const EVENTOS_PAGABLES_JORNAL = [
  'asistio',
  'asistencia_completa',
  'feriado',
  'feriado_laborado',
  'descanso',
  'descanso_laborado',
  'licencia_con_goce_de_haber',
  'vacaciones',
  'tardanza',
  'descanso_medico',
  'reposo_medico',
  'subsidio_por_enfermedad',
];

export const EVENTOS_NO_PAGABLES_JORNAL = [
  'falta',
  'permiso',
  'suspension',
];

const SET_PAGABLES = new Set(EVENTOS_PAGABLES_JORNAL);
const SET_FALTA_PERMISO = new Set(['falta', 'permiso', 'suspension']);

export function normalizarOverrideDominical(raw) {
  if (raw === true || raw === 1 || raw === '1' || raw === 'si' || raw === 'true') return true;
  if (raw === false || raw === 0 || raw === '0' || raw === 'no' || raw === 'false') return false;
  return null;
}

export function resolverDescuentoDominical({ empresaActiva, override } = {}) {
  const forzado = normalizarOverrideDominical(override);
  if (forzado === false) return false;
  if (forzado === true) return true;
  return Boolean(empresaActiva);
}

export function esFechaIsoCompleta(valor) {
  const iso = String(valor || '').split('T')[0];
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return false;
  const [y, m, d] = iso.split('-').map(Number);
  if (y < 1900 || y > 2100 || m < 1 || m > 12 || d < 1 || d > 31) return false;
  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
}

export function fechaMitadPeriodo(inicio, fin) {
  const a = toYmd(inicio);
  const b = toYmd(fin);
  if (!a) return '';
  if (!b || a > b) return a;
  const dias = enumerarDiasYmd(a, b);
  if (!dias.length) return a;
  return dias[Math.floor(dias.length / 2)] || a;
}

export function toYmd(fecha) {
  if (!fecha) return null;
  if (typeof fecha === 'string') return fecha.split('T')[0].slice(0, 10);
  try {
    const y = fecha.getFullYear();
    const m = String(fecha.getMonth() + 1).padStart(2, '0');
    const d = String(fecha.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  } catch {
    return null;
  }
}

export function diasCalendarioDelMes(ymd) {
  const iso = toYmd(ymd);
  if (!iso) return 30;
  const [y, m] = iso.split('-').map(Number);
  if (!y || !m) return 30;
  return new Date(y, m, 0).getDate();
}

export function addDaysYmd(ymd, delta) {
  const iso = toYmd(ymd);
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d + Number(delta || 0));
  return toYmd(dt);
}

export function enumerarDiasYmd(inicio, fin) {
  const a = toYmd(inicio);
  const b = toYmd(fin);
  if (!a || !b || a > b) return [];
  const out = [];
  let cur = a;
  while (cur <= b) {
    out.push(cur);
    cur = addDaysYmd(cur, 1);
  }
  return out;
}

export function normalizarTipoEvento(tipo) {
  return String(tipo || '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_');
}

export function esEventoPagableJornal(tipo) {
  return SET_PAGABLES.has(normalizarTipoEvento(tipo));
}

/** El 0121 de jornal no incluye vacaciones: esas van al 0118. */
export function esEventoPagableJornal0121(tipo) {
  return esEventoPagableJornal(tipo) && normalizarTipoEvento(tipo) !== 'vacaciones';
}

export function esEventoFaltaPermiso(tipo) {
  return SET_FALTA_PERMISO.has(normalizarTipoEvento(tipo));
}

export function normalizarFormaPagoFicha(valor) {
  return String(valor || '').toLowerCase() === FORMA_PAGO_JORNAL
    ? FORMA_PAGO_JORNAL
    : FORMA_PAGO_MENSUAL;
}

export function formaPagoFichaDeEmpleado(emp) {
  return normalizarFormaPagoFicha(
    emp?.forma_pago
    || emp?.datosSalariales?.formaPago
    || emp?.datosSalariales?.forma_pago
    || emp?.datosEmpleado?.forma_pago
  );
}

/** Personal de ficha jornal, excluyendo CONSU. */
export function esPersonalJornalNoConstruccion(emp, esConsu = false) {
  if (esConsu) return false;
  return formaPagoFichaDeEmpleado(emp) === FORMA_PAGO_JORNAL;
}

export function normalizarOverridePlanilla(valor) {
  const v = String(valor || '').toLowerCase();
  if (v === FORMA_PAGO_JORNAL || v === FORMA_PAGO_MENSUAL || v === FORMA_PAGO_MIXTO) return v;
  return null;
}

/**
 * @returns {{ modo: string, aplica: boolean, fechaDesdeJornal: string|null }}
 */
export function resolverModoPagoJornal({
  esConsu = false,
  formaPagoFicha,
  formaPagoPlanilla,
  fechaDesdeJornal,
} = {}) {
  if (esConsu) {
    return { modo: FORMA_PAGO_MENSUAL, aplica: false, fechaDesdeJornal: null };
  }
  const override = normalizarOverridePlanilla(formaPagoPlanilla);
  if (override === FORMA_PAGO_JORNAL) {
    return { modo: FORMA_PAGO_JORNAL, aplica: true, fechaDesdeJornal: null };
  }
  if (override === FORMA_PAGO_MENSUAL) {
    return { modo: FORMA_PAGO_MENSUAL, aplica: false, fechaDesdeJornal: null };
  }
  if (override === FORMA_PAGO_MIXTO) {
    const fecha = toYmd(fechaDesdeJornal);
    if (!fecha) {
      return { modo: FORMA_PAGO_MENSUAL, aplica: false, fechaDesdeJornal: null };
    }
    return { modo: FORMA_PAGO_MIXTO, aplica: true, fechaDesdeJornal: fecha };
  }
  if (normalizarFormaPagoFicha(formaPagoFicha) === FORMA_PAGO_JORNAL) {
    return { modo: FORMA_PAGO_JORNAL, aplica: true, fechaDesdeJornal: null };
  }
  return { modo: FORMA_PAGO_MENSUAL, aplica: false, fechaDesdeJornal: null };
}

export function clipRangoLaboral(fechaInicio, fechaFin, ingreso, cese) {
  let a = toYmd(fechaInicio);
  let b = toYmd(fechaFin);
  const ing = toYmd(ingreso);
  const ces = toYmd(cese);
  if (!a || !b) return null;
  if (a > b) return null;
  if (ing && ing > a) a = ing;
  if (ces && ces < b) b = ces;
  if (a > b) return null;
  return { inicio: a, fin: b };
}

function fechasLaboralesDesdeHistorial(historial) {
  const registros = historial?.registros || historial?.historial || [];
  if (Array.isArray(registros) && registros.length) {
    const vigente = [...registros].sort((x, y) => {
      const fa = toYmd(y.fecha_ingreso) || '';
      const fb = toYmd(x.fecha_ingreso) || '';
      return fa.localeCompare(fb);
    })[0];
    return {
      ingreso: toYmd(vigente?.fecha_ingreso) || toYmd(historial?.fechaIngresoActual),
      cese: toYmd(vigente?.fecha_cese) || toYmd(historial?.fechaCeseActual),
    };
  }
  return {
    ingreso: toYmd(historial?.fechaIngresoActual || historial?.fecha_ingreso),
    cese: toYmd(historial?.fechaCeseActual || historial?.fecha_cese),
  };
}

export function rangoPeriodoPlanilla(analisisPeriodo, historial) {
  const lab = fechasLaboralesDesdeHistorial(historial);
  return clipRangoLaboral(
    analisisPeriodo?.fechaInicio,
    analisisPeriodo?.fechaFin,
    lab.ingreso,
    lab.cese
  );
}

function mapaTareosPorFecha(tareos = []) {
  const mapa = new Map();
  (tareos || []).forEach((t) => {
    const f = toYmd(t.fecha || t.fecha_tareo);
    if (!f) return;
    mapa.set(f, t);
  });
  return mapa;
}

export function contarDiasPagablesJornal({
  tareos = [],
  fechaInicio,
  fechaFin,
} = {}) {
  const a = toYmd(fechaInicio);
  const b = toYmd(fechaFin);
  if (!a || !b || a > b) return 0;
  const mapa = mapaTareosPorFecha(tareos);
  let n = 0;
  enumerarDiasYmd(a, b).forEach((dia) => {
    const t = mapa.get(dia);
    if (!t) return;
    if (esEventoPagableJornal0121(t.tipo_evento)) n += 1;
  });
  return n;
}

export function contarDiasVacacionesEnRango({
  tareos = [],
  fechaInicio,
  fechaFin,
} = {}) {
  const a = toYmd(fechaInicio);
  const b = toYmd(fechaFin);
  if (!a || !b || a > b) return 0;
  let n = 0;
  (tareos || []).forEach((t) => {
    const f = toYmd(t.fecha || t.fecha_tareo);
    if (!f || f < a || f > b) return;
    if (normalizarTipoEvento(t.tipo_evento) === 'vacaciones') n += 1;
  });
  return n;
}

export function contarFaltasPermisosEnRango({
  tareos = [],
  fechaInicio,
  fechaFin,
} = {}) {
  const a = toYmd(fechaInicio);
  const b = toYmd(fechaFin);
  if (!a || !b || a > b) return 0;
  let n = 0;
  (tareos || []).forEach((t) => {
    const f = toYmd(t.fecha || t.fecha_tareo);
    if (!f || f < a || f > b) return;
    if (esEventoFaltaPermiso(t.tipo_evento)) n += 1;
  });
  return n;
}

export function esConceptoAfectoPagoJornal(concepto, conceptosAfectosTareos = []) {
  const codigo = String(concepto?.plame_configuracion?.codigo_plame || concepto?.codigo_plame || '');
  if (codigo === '0121') return true;
  const id = Number(concepto?.plame_configuracion?.id || concepto?.plame_configuracion_id);
  if (!id) return false;
  return (conceptosAfectosTareos || []).some(
    (row) => Number(row.id_concepto_plame) === id
  );
}

export function redondear2(n) {
  return parseFloat((Number(n) || 0).toFixed(2));
}

export function esDescuentoDominicalJornal(concepto) {
  if (!concepto) return false;
  const origen = String(concepto.origenCalculo || concepto.origen_calculo || concepto.origen || '');
  if (origen === ORIGEN_DESCUENTO_DOMINICAL_JORNAL) return true;
  const codigo = String(concepto.codigoPlame || concepto.codigo_plame || concepto.codigo || '');
  return codigo === CODIGO_DESCUENTO_DOMINICAL_JORNAL;
}

export function esDescuentoTardanzaJornal(concepto) {
  if (!concepto) return false;
  const origen = String(concepto.origenCalculo || concepto.origen_calculo || concepto.origen || '');
  if (origen === ORIGEN_DESCUENTO_TARDANZA_JORNAL) return true;
  const codigo = String(concepto.codigoPlame || concepto.codigo_plame || concepto.codigo || '');
  return codigo === CODIGO_DESCUENTO_TARDANZA_JORNAL;
}

export function esDescuentoAbsorbidoEn0121Jornal(concepto) {
  if (!concepto || concepto.fusionarEn0705 || concepto.absorberEn0121 === false) return false;
  return esDescuentoDominicalJornal(concepto) || esDescuentoTardanzaJornal(concepto);
}

/** Dominical/tardanzas de vista (jornal o mensual) que bajan ONP/AFP/EsSalud. */
export function esDescuentoVistaQueReduceBIO(concepto) {
  return esDescuentoDominicalJornal(concepto) || esDescuentoTardanzaJornal(concepto);
}

export function montoDominicalFusionarEn0705(empleadoODescuentos) {
  const descuentos = Array.isArray(empleadoODescuentos)
    ? empleadoODescuentos
    : (empleadoODescuentos?.conceptos?.descuentos || []);
  return redondear2((descuentos || []).reduce((suma, concepto) => {
    if (!concepto?.fusionarEn0705) return suma;
    return suma + (Number(concepto.monto) || 0);
  }, 0));
}

export function montoAbsorbido0121Jornal(meta) {
  return redondear2(
    (Number(meta?.montoDominical) || 0) + (Number(meta?.montoTardanzas) || 0)
  );
}

/** Horas de domingo perdidas por faltas (8/7 × faltas) en el rango, misma regla mensual. */
export function horasDominicalPorFaltasEnRango({
  tareos = [],
  fechaInicio,
  fechaFin,
} = {}) {
  const a = toYmd(fechaInicio);
  const b = toYmd(fechaFin);
  if (!a || !b || a > b) return 0;
  const enRango = (tareos || [])
    .map((t) => ({ ...t, _f: toYmd(t.fecha || t.fecha_tareo) }))
    .filter((t) => t._f && t._f >= a && t._f <= b)
    .sort((x, y) => x._f.localeCompare(y._f));
  if (!enRango.length) return 0;

  let horas = 0;
  let semana = [];
  const cerrarSemana = () => {
    if (!semana.length) return;
    const ini = semana[0]._f;
    const fin = semana[semana.length - 1]._f;
    if (ini !== fin) {
      const faltas = semana.filter((t) => normalizarTipoEvento(t.tipo_evento) === 'falta').length;
      horas += (8 / 7) * faltas;
    }
    semana = [];
  };

  enRango.forEach((t, i) => {
    semana.push(t);
    const [y, m, d] = t._f.split('-').map(Number);
    const esDomingo = new Date(y, m - 1, d).getDay() === 0;
    if (esDomingo || i === enRango.length - 1) cerrarSemana();
  });
  return horas > 0 ? horas : 0;
}

export function montoDominicalDesdeJornalDiario(jornalDiario, horas) {
  const j = Number(jornalDiario) || 0;
  const h = Number(horas) || 0;
  if (j <= 0 || h <= 0) return 0;
  return redondear2((j / 8) * h);
}

export function construirDescuentoDominicalVista(monto, opts = {}) {
  const n = redondear2(monto);
  const fusionarEn0705 = Boolean(opts.fusionarEn0705);
  const absorberEn0121 = fusionarEn0705 ? false : opts.absorberEn0121 !== false;
  return {
    codigoPlame: CODIGO_DESCUENTO_DOMINICAL_JORNAL,
    codigo: CODIGO_DESCUENTO_DOMINICAL_JORNAL,
    codigo_plame: CODIGO_DESCUENTO_DOMINICAL_JORNAL,
    concepto: 'DESCUENTO DOMINICAL',
    descripcion: 'DESCUENTO DOMINICAL',
    monto: n,
    tipo_concepto: 'descuento',
    origenCalculo: ORIGEN_DESCUENTO_DOMINICAL_JORNAL,
    origen_calculo: ORIGEN_DESCUENTO_DOMINICAL_JORNAL,
    exportable_plame: false,
    excluirRem: true,
    absorberEn0121,
    fusionarEn0705,
  };
}

export function construirDescuentoTardanzaVista(monto) {
  const n = redondear2(monto);
  return {
    codigoPlame: CODIGO_DESCUENTO_TARDANZA_JORNAL,
    codigo: CODIGO_DESCUENTO_TARDANZA_JORNAL,
    codigo_plame: CODIGO_DESCUENTO_TARDANZA_JORNAL,
    concepto: 'TARDANZAS',
    descripcion: 'TARDANZAS',
    monto: n,
    tipo_concepto: 'descuento',
    origenCalculo: ORIGEN_DESCUENTO_TARDANZA_JORNAL,
    origen_calculo: ORIGEN_DESCUENTO_TARDANZA_JORNAL,
    exportable_plame: false,
    excluirRem: true,
    absorberEn0121: true,
  };
}

export function textoAclaracionPagoJornal(meta) {
  if (!meta?.aplica) return '';
  const dias = Number(meta.diasPagables) || 0;
  const jornal = Number(meta.jornal0121);
  const diasTxt = `${dias} día${dias === 1 ? '' : 's'}`;
  if (meta.modo === FORMA_PAGO_MIXTO) {
    const dMens = Number(meta.diasMensual) || 0;
    const dJor = Number(meta.diasPagables) || 0;
    let txt = `Pago mixto: ${dMens} día${dMens === 1 ? '' : 's'} mensual y ${dJor} día${dJor === 1 ? '' : 's'} por jornal.`;
    if (meta.sueldo1 != null && meta.sueldo2 != null && meta.fechaCambioSueldo) {
      const s1 = Number(meta.sueldo1).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const s2 = Number(meta.sueldo2).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const [y, m, d] = String(meta.fechaCambioSueldo).split('-');
      txt += ` Cambio de sueldo: S/ ${s1} hasta el día anterior y S/ ${s2} desde el ${d}/${m}/${y}.`;
    }
    return txt;
  }
  if (Number.isFinite(jornal) && jornal > 0) {
    const jornalTxt = jornal.toLocaleString('es-PE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const vac = Number(meta.diasVacaciones) || 0;
    const vacTxt = vac > 0
      ? ` ${vac} día${vac === 1 ? '' : 's'} de vacaciones van al 0118.`
      : '';
    const base = `Pago por jornal: ${diasTxt} × S/ ${jornalTxt}.${vacTxt}`;
    const extras = [];
    const dom = Number(meta.montoDominical) || 0;
    const tar = Number(meta.montoTardanzas) || 0;
    if (dom > 0) {
      extras.push(`DESCUENTO DOMINICAL S/ ${dom.toLocaleString('es-PE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`);
    }
    if (tar > 0) {
      extras.push(`TARDANZAS S/ ${tar.toLocaleString('es-PE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`);
    }
    if (extras.length) {
      return `${base} ${extras.join('. ')} (se restan del 0121 al declarar).`;
    }
    return base;
  }
  return `Pago por jornal: ${diasTxt}.`;
}

/**
 * Reescribe montos de conceptos afectos a tareo según jornal / mixto.
 * No toca conceptos estáticos.
 */
export function sueldoVigenteEnFecha(ymd, sueldoTramo, fallback) {
  const f = toYmd(ymd);
  const sFallback = Number(fallback) || 0;
  const corte = toYmd(sueldoTramo?.fecha_cambio);
  if (!f || !corte) return sFallback;
  if (f >= corte) return Number(sueldoTramo.sueldo_2) || 0;
  return Number(sueldoTramo.sueldo_1) || 0;
}

export function aplicarPagoJornalAConceptos({
  conceptosIngresos = [],
  conceptosAfectosTareos = [],
  conceptosEstaticos = [],
  analisisPeriodo,
  tareos = [],
  historial,
  modo,
  fechaDesdeJornal,
  esEstaticoFn,
  descontarDescansos = false,
  sueldoTramo = null,
} = {}) {
  const rango = rangoPeriodoPlanilla(analisisPeriodo, historial);
  const fechaRef = rango?.inicio || toYmd(analisisPeriodo?.fechaInicio);
  const diasMes = diasCalendarioDelMes(fechaRef);
  const divisorMensual = 30;

  const esEstatico = (concepto) => {
    const codigo = String(
      concepto?.plame_configuracion?.codigo_plame
      || concepto?.codigo_plame
      || concepto?.codigo
      || ''
    );
    if (esAsignacionFamiliarPlame(codigo)) return true;
    if (typeof esEstaticoFn === 'function') return esEstaticoFn(concepto);
    return (conceptosEstaticos || []).some(
      (c) => String(c.codigo || c.codigo_plame) === codigo
    );
  };

  let diasPagables = 0;
  let diasMensual = 0;
  let rangoJornal = null;
  let rangoMensual = null;

  if (modo === FORMA_PAGO_JORNAL && rango) {
    rangoJornal = rango;
    diasPagables = contarDiasPagablesJornal({
      tareos,
      fechaInicio: rango.inicio,
      fechaFin: rango.fin,
    });
  } else if (modo === FORMA_PAGO_MIXTO && rango) {
    const corte = toYmd(fechaDesdeJornal);
    const finMensual = addDaysYmd(corte, -1);
    if (finMensual && finMensual >= rango.inicio) {
      rangoMensual = {
        inicio: rango.inicio,
        fin: finMensual < rango.fin ? finMensual : rango.fin,
      };
      if (rangoMensual.inicio <= rangoMensual.fin) {
        diasMensual = enumerarDiasYmd(rangoMensual.inicio, rangoMensual.fin).length;
      } else {
        rangoMensual = null;
        diasMensual = 0;
      }
    }
    if (corte && corte <= rango.fin) {
      rangoJornal = {
        inicio: corte > rango.inicio ? corte : rango.inicio,
        fin: rango.fin,
      };
      diasPagables = contarDiasPagablesJornal({
        tareos,
        fechaInicio: rangoJornal.inicio,
        fechaFin: rangoJornal.fin,
      });
    }
  }

  const faltasPermisosMensual = rangoMensual
    ? contarFaltasPermisosEnRango({
      tareos,
      fechaInicio: rangoMensual.inicio,
      fechaFin: rangoMensual.fin,
    })
    : 0;

  const diasVacaciones = rangoJornal
    ? contarDiasVacacionesEnRango({
      tareos,
      fechaInicio: rangoJornal.inicio,
      fechaFin: rangoJornal.fin,
    })
    : 0;

  let jornal0121 = 0;
  let base0705 = 0;
  const conceptos = (conceptosIngresos || []).map((concepto) => {
    if (esEstatico(concepto)) return concepto;
    if (!esConceptoAfectoPagoJornal(concepto, conceptosAfectosTareos)) return concepto;
    const monto = Number(concepto.monto) || 0;
    base0705 += monto;
    const jornal = diasMes > 0 ? monto / diasMes : 0;
    const codigo = String(concepto?.plame_configuracion?.codigo_plame || '');
    if (codigo === '0121') {
      const refJornal = rangoJornal?.inicio || fechaRef;
      jornal0121 = diasMes > 0
        ? sueldoVigenteEnFecha(refJornal, sueldoTramo, monto) / diasMes
        : jornal;
    }

    let nuevo = 0;
    if (codigo === '0121' && sueldoTramo?.fecha_cambio) {
      const divisorMensualLocal = 30;
      let parteMensual = 0;
      if (modo === FORMA_PAGO_MIXTO && rangoMensual) {
        enumerarDiasYmd(rangoMensual.inicio, rangoMensual.fin).forEach((dia) => {
          parteMensual += sueldoVigenteEnFecha(dia, sueldoTramo, monto) / divisorMensualLocal;
        });
      }
      let parteJornal = 0;
      const rangoPago = modo === FORMA_PAGO_JORNAL ? rango : rangoJornal;
      if (rangoPago && diasMes > 0) {
        const mapa = mapaTareosPorFecha(tareos);
        enumerarDiasYmd(rangoPago.inicio, rangoPago.fin).forEach((dia) => {
          const t = mapa.get(dia);
          if (!t || !esEventoPagableJornal0121(t.tipo_evento)) return;
          parteJornal += sueldoVigenteEnFecha(dia, sueldoTramo, monto) / diasMes;
        });
      }
      nuevo = parteMensual + parteJornal;
    } else if (modo === FORMA_PAGO_JORNAL) {
      nuevo = jornal * diasPagables;
    } else if (modo === FORMA_PAGO_MIXTO) {
      const parteMensual = (monto / divisorMensual) * diasMensual;
      const parteJornal = jornal * diasPagables;
      nuevo = parteMensual + parteJornal;
    } else {
      return concepto;
    }

    return {
      ...concepto,
      monto: redondear2(nuevo),
      monto_jornal_aplicado: true,
      jornal_diario: redondear2(jornal),
    };
  });

  const c0121Aplicado = conceptos.find(
    (c) => String(c.plame_configuracion?.codigo_plame || c.codigo_plame) === '0121'
  );
  const bruto0121 = Number(c0121Aplicado?.monto) || 0;
  let horasDominical = 0;
  let montoDominical = 0;
  if (descontarDescansos && rangoJornal && jornal0121 > 0) {
    horasDominical = horasDominicalPorFaltasEnRango({
      tareos,
      fechaInicio: rangoJornal.inicio,
      fechaFin: rangoJornal.fin,
    });
    montoDominical = montoDominicalDesdeJornalDiario(jornal0121, horasDominical);
  }
  const sueldo0121Rem = redondear2(Math.max(0, bruto0121 - montoDominical));

  const meta = {
    aplica: true,
    modo,
    diasMes,
    diasPagables,
    diasMensual,
    diasVacaciones,
    jornal0121: redondear2(jornal0121),
    skipProporcional: true,
    skip0705: modo === FORMA_PAGO_JORNAL,
    skipDescansos: true,
    faltasPermisosMensual,
    base0705: redondear2(base0705),
    rangoJornal,
    rangoMensual,
    fechaDesdeJornal: toYmd(fechaDesdeJornal),
    sueldo1: sueldoTramo?.sueldo_1 != null ? Number(sueldoTramo.sueldo_1) : null,
    sueldo2: sueldoTramo?.sueldo_2 != null ? Number(sueldoTramo.sueldo_2) : null,
    fechaCambioSueldo: toYmd(sueldoTramo?.fecha_cambio),
    horasDominical: redondear2(horasDominical),
    montoDominical,
    sueldo0121Rem,
    descontarDescansos: Boolean(descontarDescansos),
  };
  meta.aclaracion = textoAclaracionPagoJornal(meta);
  return { conceptos, meta };
}

export function aplicarPagoJornalEnCalculo(empleado, datosDisponibles, opts = {}) {
  const resuelto = resolverModoPagoJornal({
    esConsu: Boolean(opts.esConsu),
    formaPagoFicha:
      empleado?.forma_pago
      || empleado?.datosSalariales?.formaPago
      || empleado?.datosEmpleado?.forma_pago,
    formaPagoPlanilla:
      empleado?.forma_pago_planilla
      || empleado?.datosSalariales?.formaPagoPlanilla,
    fechaDesdeJornal:
      empleado?.fecha_desde_jornal
      || empleado?.datosSalariales?.fechaDesdeJornal,
  });

  if (!resuelto.aplica) {
    empleado.pagoJornal = { aplica: false, modo: resuelto.modo };
    return datosDisponibles;
  }

  const descontarDescansos = Boolean(
    opts.descontarDescansos
    ?? datosDisponibles.descansosSemanales?.configuracionActivada
    ?? empleado.datosAPIs?.configuracionTareo?.descontar_descansos
    ?? empleado.descuento_descanso
    ?? empleado.datosEmpleado?.descuento_descanso
  );

  const { conceptos, meta } = aplicarPagoJornalAConceptos({
    conceptosIngresos: datosDisponibles.conceptosIngresos,
    conceptosAfectosTareos: datosDisponibles.apis?.conceptosAfectosTareos || [],
    conceptosEstaticos: datosDisponibles.conceptosEstaticos?.completos || [],
    analisisPeriodo: datosDisponibles.analisisPeriodo,
    tareos: datosDisponibles.tareos?.tareosDetallados || [],
    historial: datosDisponibles.historialLaboral,
    modo: resuelto.modo,
    fechaDesdeJornal: resuelto.fechaDesdeJornal,
    esEstaticoFn: opts.esEstaticoFn,
    descontarDescansos,
    sueldoTramo: opts.sueldoTramo || null,
  });

  datosDisponibles.conceptosIngresos = conceptos;
  const c0121 = conceptos.find((c) => String(c.plame_configuracion?.codigo_plame) === '0121');
  if (c0121) {
    const bruto = Number(c0121.monto) || 0;
    const absorbed = montoAbsorbido0121Jornal(meta);
    datosDisponibles.sueldo = absorbed > 0
      ? redondear2(Math.max(0, bruto - absorbed))
      : bruto;
    if (empleado.datosSalariales) {
      empleado.datosSalariales.sueldoBasico = bruto;
    }
  }
  empleado.pagoJornal = meta;
  return datosDisponibles;
}
